import { normalizeSearch } from "./search-normalization";
import { resolveSearchIntent } from "./search-intents";
import { joinsProductBrand } from "./search-brands";
import type { ProductView } from "./types";

export const MAX_SEARCH_ITEMS = 12;
export const MAX_SEARCH_TEXT = 600;
/** HTML search inputs strip line breaks before input events. Preserve the list
 * structure on paste without changing ordinary single-line clipboard content. */
export function productSearchPasteText(text: string): string {
  return text.replace(/\r\n|[\r\n]/g, "; ");
}
export interface ProductSearchQuery {
  items: string[];
  multiple: boolean;
  error?: "items" | "length";
}
const descriptor = /^(?:\d+(?:[.,]\d+)?\s*(?:%|кг|kg|г|g|гр|gr|мл|ml|л|l|шт\.?|buc\.?|lei|ле[йя]|грн|mdl)(?:\s*(?:жирности|жирність|grasime|[x×*]\s*\d+(?:[.,]\d+)?\s*(?:г|g|мл|ml|л|l)))?\s*)+$/iu;
function knownItem(text: string): boolean {
  if (resolveSearchIntent(text)) return true;
  const withoutAmount = text.replace(/\d+(?:[.,]\d+)?\s*(?:%|кг|kg|г|g|гр|gr|мл|ml|л|l|шт\.?|buc\.?)\s*(?:жирности|жирність|grasime)?/giu, " ").trim();
  return Boolean(resolveSearchIntent(withoutAmount));
}
function explicitNouns(text: string): string[] {
  if (knownItem(text)) return [text];
  const words = text.split(/\s+/u);
  if (words.length > MAX_SEARCH_ITEMS * 3) return [text];
  const parts: string[] = [];
  for (let cursor = 0; cursor < words.length;) {
    let found = false;
    for (let length = Math.min(4, words.length - cursor); length > 0; length -= 1) {
      const phrase = words.slice(cursor, cursor + length).join(" ");
      if (!resolveSearchIntent(phrase)) continue;
      parts.push(phrase); cursor += length; found = true; break;
    }
    if (!found) return [text];
  }
  return parts.length > 1 ? parts : [text];
}

/** Parse only explicit lists. Decimal commas, quoted names and compound product
 * names are preserved. A conjunction splits only complete known shopping nouns,
 * so "Head and Shoulders" / "корм для кошек и собак" remain one query. */
export function parseProductSearchQuery(input: string, products: readonly ProductView[] = []): ProductSearchQuery {
  const raw = input.trim();
  if (raw.length > MAX_SEARCH_TEXT) return { items: [], multiple: false, error: "length" };
  const chunks: Array<{ text: string; hardBefore: boolean }> = [];
  let current = "";
  let hardBefore = false;
  let quote = "";
  let depth = 0;
  for (let index = 0; index < raw.length; index += 1) {
    const char = raw[index]!;
    if (quote) {
      if (char === quote) quote = "";
      current += char;
      continue;
    }
    if (char === '"' || char === "«" || char === "“") {
      quote = char === "«" ? "»" : char === "“" ? "”" : char;
      current += char;
      continue;
    }
    if (char === "(" || char === "[") depth += 1;
    if (char === ")" || char === "]") depth = Math.max(0, depth - 1);
    const decimal = char === "," && /\d/u.test(raw[index - 1] ?? "") && /\d/u.test(raw[index + 1] ?? "");
    if (!depth && (char === ";" || char === "\n" || char === "\r" || (char === "," && !decimal))) {
      if (current.trim()) { chunks.push({ text: current.trim(), hardBefore }); hardBefore = false; }
      current = "";
      hardBefore ||= char !== ",";
    } else current += char;
  }
  if (current.trim()) chunks.push({ text: current.trim(), hardBefore });
  // A pasted single product title often has comma-separated pack/fat values.
  // These are constraints on the previous item, not extra shopping requests.
  const merged: string[] = [];
  for (const { text: chunk, hardBefore } of chunks) {
    const previous = merged.at(-1);
    if (previous && !hardBefore && descriptor.test(chunk)) merged[merged.length - 1] += `, ${chunk}`;
    else if (previous && !hardBefore && joinsProductBrand(products, previous, chunk)) merged[merged.length - 1] += ` ${chunk}`;
    else merged.push(chunk);
  }
  const items: string[] = [];
  const seen = new Set<string>();
  for (const chunk of merged) {
    const parts = chunk.split(/\s+(?:и|і|та|și|si|and)\s+/iu);
    const protectedName = /["«“(\[]/u.test(chunk);
    const split = protectedName ? [chunk] : parts.length > 1 && parts.every(knownItem) ? parts : explicitNouns(chunk);
    for (const part of split) {
      const item = part.replace(/^[\s•●*-]+/u, "").replace(/^\d+[.)]\s+/u, "").trim();
      if (!normalizeSearch(item).replace(/\./g, "")) continue;
      const key = resolveSearchIntent(item)?.id ?? normalizeSearch(item);
      if (!seen.has(key)) { seen.add(key); items.push(item); }
    }
  }
  if (items.length > MAX_SEARCH_ITEMS) return { items: [], multiple: true, error: "items" };
  return { items, multiple: items.length > 1 };
}
